import { useState, type FormEvent } from "react";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const FORM_ENDPOINT = "https://readdy.ai/api/form/dahg543oh653ivfvnu1g";

const platformOptions = ["抖音", "快手", "小红书", "B站", "微博", "视频号", "淘宝直播", "其他平台"];
const categoryOptions = [
  "私信截图",
  "聊天记录",
  "带货售后",
  "直播事故",
  "人设争议",
  "商务纠纷",
];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors";

const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

export default function SubmitForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [detailLength, setDetailLength] = useState(0);
  const [tipCode, setTipCode] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("contact_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      setTipCode("JG-" + Math.floor(100000 + Math.random() * 899999));
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) payload.append(key, text);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        setTipCode("JG-" + Math.floor(100000 + Math.random() * 899999));
        form.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "投稿未能通过系统校验，请检查内容后重试。"
            : String(serverMsg) || "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，投稿未送达。请检查网络后重试，你的内容仍然保留。");
    }
  };

  return (
    <section id="submit" className="w-full bg-foreground-950 py-14 md:py-24">
      <div className="w-full px-4 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 text-xs md:text-sm font-medium tracking-[0.15em] text-accent-400">
              <span className="w-6 h-px bg-accent-400"></span>
              匿名投稿入口
            </span>
            <h2 className="mt-3 md:mt-4 font-heading text-2xl md:text-4xl font-bold leading-snug text-background-50">
              把你的线索，交给核验编辑组
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-background-200">
              填写得越具体，核验越顺利。联系方式可以完全留空，我们依然会处理你的线索；若愿意配合补充材料，请留下常用邮箱，方便编辑组回访。
            </p>

            <ul className="mt-7 md:mt-9 flex flex-col gap-4">
              {[
                { icon: "ri-lock-2-line", text: "全流程匿名，不记录投稿人身份" },
                { icon: "ri-image-line", text: "支持说明私信截图、聊天记录的时间与来源" },
                { icon: "ri-time-line", text: "72 小时内给出初步判断" },
                { icon: "ri-delete-bin-6-line", text: "未通过核验的材料不做任何公开" },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3 text-sm text-background-100">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-background-50/10 border border-background-50/15 text-accent-400 flex items-center justify-center">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <span className="pt-1.5">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-background-50 rounded-lg p-5 md:p-8">
              {status === "success" ? (
                <div className="py-8 md:py-12 text-center">
                  <span className="mx-auto w-16 h-16 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-4xl"></i>
                  </span>
                  <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    线索已收到，感谢你的勇气
                  </h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed max-w-md mx-auto">
                    编辑组将在 72 小时内完成初步判断。你的匿名编号为
                    <strong className="text-primary-600"> {tipCode}</strong>
                    ，请妥善保存，后续可凭此编号查询进度。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    再提交一条线索
                  </button>
                </div>
              ) : (
                <form
                  id="tip-submit-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass} htmlFor="subject">
                        爆料对象 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        maxLength={60}
                        placeholder="如：某平台美妆博主（可写平台+领域）"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="platform">
                        涉及平台 <span className="text-primary-500">*</span>
                      </label>
                      <select id="platform" name="platform" required defaultValue="" className={inputClass}>
                        <option value="" disabled>
                          请选择平台
                        </option>
                        {platformOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass} htmlFor="category">
                      线索类型 <span className="text-primary-500">*</span>
                    </label>
                    <select id="category" name="category" required defaultValue="" className={inputClass}>
                      <option value="" disabled>
                        请选择线索类型
                      </option>
                      {categoryOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass} htmlFor="detail">
                      事件经过 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="detail"
                      name="detail"
                      required
                      maxLength={500}
                      rows={5}
                      onChange={(e) => setDetailLength(e.target.value.length)}
                      placeholder="请按时间顺序描述事件：何时、发生了什么、涉及哪些人。若提及截图或聊天记录，请说明其来源与完整程度。"
                      className={`${inputClass} resize-none leading-relaxed`}
                    ></textarea>
                    <p className="mt-1.5 text-right text-xs text-foreground-400">{detailLength}/500</p>
                  </div>

                  <fieldset>
                    <legend className={labelClass}>你手上有哪些原始凭证？</legend>
                    <div className="flex flex-wrap gap-2.5">
                      {["完整聊天导出", "部分截图", "录屏文件", "订单与后台", "暂无可提供材料"].map((opt) => (
                        <label
                          key={opt}
                          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-background-300 text-sm text-foreground-700 has-[:checked]:border-primary-400 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-700 cursor-pointer transition-colors"
                        >
                          <input type="radio" name="evidence" value={opt} className="accent-primary-500" />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset>
                    <legend className={labelClass}>是否愿意配合后续核验？</legend>
                    <div className="flex flex-wrap gap-2.5">
                      {["愿意补充材料", "仅限线上沟通", "希望完全匿名"].map((opt) => (
                        <label
                          key={opt}
                          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-background-300 text-sm text-foreground-700 has-[:checked]:border-secondary-400 has-[:checked]:bg-secondary-50 has-[:checked]:text-secondary-800 cursor-pointer transition-colors"
                        >
                          <input type="radio" name="cooperate" value={opt} className="accent-secondary-500" />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div>
                    <label className={labelClass} htmlFor="email">
                      联系方式（选填）
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="用于编辑组回访，可留空"
                      className={inputClass}
                    />
                  </div>

                  <label className="flex items-start gap-3 text-xs md:text-sm text-foreground-600 cursor-pointer">
                    <input
                      type="checkbox"
                      name="agree"
                      value="已阅读并同意"
                      required
                      className="mt-0.5 accent-primary-500"
                    />
                    <span>
                      我确认所提交内容真实，并已阅读投稿规范；理解虚假或恶意内容将被拒绝处理。
                    </span>
                  </label>

                  <div className="contact-field-guard" aria-hidden="true">
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </div>

                  {status === "error" && formError && (
                    <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-3">
                      <i className="ri-error-warning-line text-lg leading-5"></i>
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"}></i>
                    {status === "submitting" ? "正在加密提交…" : "匿名提交线索"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}